<template>
<div id="o-base-app" data-component="CustomerApp.vue" :data-is-virtual-keyboard-visible="isVirtualKeyboardVisible">
	<div
		v-if="isScreenDimmed"
		class="is-dimmed | md:g-black/25 md:fixed md:inset-0 md:z-[4096] md:backdrop-blur-sm"
		@click="isScreenDimmed = false"
	/>

	<AppRouterView v-if="appModule === 'RouterView'"/>
	<component :is="appModule" v-else-if="appModule" ref="CurrentModule"/>

	<div
		id="preload-placeholder"
		class="fixed inset-0 z-[4096] flex items-center justify-center bg-white/75 transition-opacity duration-300"
		:class="isSavingScreenVisible ? 'opacity-100 pointer-events-all delay-150' : 'opacity-0 pointer-events-none'"
	>
		<Loader/>
	</div>

	<WebsocketsElement v-if="websocketUrl" ref="Websockets"/>
	<ToasterElement ref="Toaster"/>

	<CustomerBase v-if="StateManager && customerId" :state="StateManager"/>

	<div
		v-if="loggedAs"
		class="fixed bottom-0 left-0 z-64 m-2 !block bg-warn p-2 text-black"
		v-html="`Zalogowany jako <strong>${loggedAs}</strong>`"
	/>
</div>
</template>

<script lang="ts">
import { mapActions, mapStores, mapWritableState, mapState } from 'pinia';
import { defineAsyncComponent } from 'vue';
import { ToasterElement } from 'ofe-common/components';

import AppRouterView from '@globals/AppRouterView.vue';
import WebsocketsElement from '@globals/components/global/WebsocketsElement.vue';
import { setPushNotificationsInstance } from '@globals/composables/pushNotifications';
import Formatters from '@globals/Formatters';
import hello from '@globals/Hello';
import PushNotifications from '@globals/PushNotifications';
import StateManager from '@globals/StateManager';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import { getAppType, isBuyerApp } from '@globals/utilities/isMobileApp';
import Validators from '@globals/Validators';

import { useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';
import { useCustomerProfileViewStore } from '@customer/stores/customerProfileView';
import { useCustomerRfpListStore } from '@customer/stores/customerRfpList';
import { useImportantMessageStore } from '@customer/stores/importantMessage';
import { usePromoMessagesStore } from '@customer/stores/promoMessages';
import { useServicesStore } from '@customer/stores/services';

import CustomerBase from './CustomerBase.vue';

export default {
	name: 'BaseApp',
	components: {
		AppRouterView,
		CustomerBase,
		ToasterElement,
		WebsocketsElement,

		CustomerView: defineAsyncComponent(() => import('@customer/CustomerView.vue')),
		PublicView: defineAsyncComponent(() => import('@customer/PublicView.vue')),
		LoginRegister: defineAsyncComponent(() => import('@customer/login-register/LoginRegister.vue')),
	},
	data() {
		return {
			StateManager: null as null | StateManager,
			Formatters: new Formatters(),
			Validators,
			Push: null as null | PushNotifications,
			IsLoading: true,
		};
	},
	computed: {
		...mapWritableState(useAppStore, { appModule: 'module' }),
		...mapWritableState(useUserStore, ['loggedAs', 'role']),
		...mapWritableState(useAppStore, [
			'websocketUrl',
			'isVirtualKeyboardVisible',
			'isScreenDimmed',
			'isSavingScreenVisible',
			'pathname',
		]),
		...mapState(useUserStore, ['customerId', 'userId']),
		...mapStores(useAppStore),
		...mapWritableState(useCustomerProfileViewStore, [
			'isVueRouterView',
		]),
	},
	async created() {
		console.log('Oferteo.pl / app-customer/CustomerApp.vue');
		await this.hello();

		this.pathname = window.location.pathname;
		this.addPopstateListener();
	},
	methods: {
		...mapActions(useAppStore, [
			'hasFlag',
			'sendStartupAnalytics',
			'mobileAppRoutine',
		]),
		...mapActions(useCustomerOnboardingStore, ['canShowOnboarding']),

		addPopstateListener() {
			const vueRouterRoutes = [
				'/konto/uslugi-produkty',
				'/konto/profil',
				'/konto/terminarz',
			];

			window.addEventListener('popstate', (e) => {
				const { StateManager: stateManager } = this;

				if (e.state && stateManager) {
					const vueRoutes = vueRouterRoutes.find((i) => this.$route.path.startsWith(i));
					if (vueRoutes) {
						return;
					}

					this.isVueRouterView = false;

					stateManager.InternalStack.pop();
					stateManager.Resolve(
						stateManager.InternalStack[
							stateManager.InternalStack.length - 1
						],
						true,
					);
				}
			});
		},

		getModule() {
			const { href } = document.location;
			let result = 'CustomerView';

			if (href.indexOf('/logowanie') > -1) {
				result = 'LoginRegister';
			}

			if (href.indexOf('/onboarding') > -1) {
				result = 'RouterView';
			}

			if (href.indexOf('/zobacz') > -1 || href.indexOf('/rfp') > -1) {
				result = 'PublicView';
			}

			if (
				href.indexOf('/konto/rejestracja') > -1 || href.indexOf('rejestracja-dostawcy.html') > -1 || href.indexOf('/wybierz-klientow') > -1
			) {
				result = 'LoginRegister';
			}

			return result;
		},

		async hello() {
			const { href, pathname } = window.location;
			const currentModule = this.getModule() || '';

			const pageWithLogin = !(currentModule !== 'CustomerView' || pathname.indexOf('/konto/zaplac') !== -1 || pathname.indexOf('/konto/polecanie') !== -1) || pathname.includes('/konto/onboarding');

			const needsLogin = await hello(pageWithLogin, async (rsvp) => {
				const { role } = rsvp;
				const isCustomer = role === 'ADMIN' || role === 'CUSTOMER';
				if (!isCustomer) { return; }

				const promoMessagesStore = usePromoMessagesStore();
				promoMessagesStore.update();

				const importantMessageStore = useImportantMessageStore();
				importantMessageStore.update(rsvp);

				const customerRfpListStore = useCustomerRfpListStore();
				customerRfpListStore.updateFlags(rsvp.flags);

				const customerOnboardingStore = useCustomerOnboardingStore();
				customerOnboardingStore.update(rsvp);

				if (rsvp.apImportantMessage) {
					const servicesStore = useServicesStore();
					servicesStore.setNotifications(rsvp.apImportantMessage);
				}
			});

			// Jeżeli to partner, to przejdź na ekran statystyk.
			if (this.role === 'PARTNER') {
				document.location.replace('/myoferteo/program-partnerski/statystyki');
				return;
			}

			let stateManagerUrl = pathname;

			if (needsLogin) {
				const buyerApp = await isBuyerApp();
				if (buyerApp) {
					document.location.replace('/buyer-app/auth/login/email');
					return;
				} else {
					this.appModule = 'LoginRegister';
					stateManagerUrl = '/logowanie';
				}

				if (pathname !== '/' && pathname !== 'logowanie' && !href.endsWith('/konto')) {
					window.localStorage['LoginRedirectTo'] = href;
				} else {
					window.localStorage['LoginRedirectTo'] = '';
				}
			} else {
				this.appModule = currentModule;
			}

			try {
				this.mobileAppRoutine();
				this.sendStartupAnalytics();
			} catch (err) {
				console.warn(err);
			}

			try {
				const appType = await getAppType();
				this.Push = new PushNotifications(this.userId, false, appType);
				setPushNotificationsInstance(this.Push);
			} catch (err) {
				console.warn(err);
			}

			// Jeżeli przechodzimy do Mojego Oferteo, ale nie wyspecyfikujemy na którą konkretnie stronę,
			// przejdź na Zapytania lub Kontakty w zależności od tego, czy mamy jakieś nieprzeczytane wiadomości.
			if (['/konto', '/konto/', '/konto/zapytania'].includes(stateManagerUrl)) {
				stateManagerUrl = `/konto/${this.appStore.hasFlag('newMessagesSinceLastVisit') ? 'kontakty' : 'zapytania'}`;
			}

			// Jeżeli jesteśmy na stronie Zapytań bądź Kontaktów, wyświetl Onboarding, o ile jest co pokazać.
			const canShowOnboarding = this.canShowOnboarding(href);
			switch (canShowOnboarding) {
				case 'autenti':
					stateManagerUrl = '/witaj';
					break;
				case 'onboarding':
					this.appModule = 'RouterView';
					stateManagerUrl = '/onboarding/witaj';
					this.$router.push({ name: 'CustomerOnboardingWelcome', replace: true });
					break;
				default: break;
			}

			// Utwórz instancję menedżera stanów.
			this.StateManager = new StateManager(stateManagerUrl);

			this.IsLoading = false;
			this.isSavingScreenVisible = false;
		},
	},
};
</script>
